import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Save, Search, Edit, Delete } from "@mui/icons-material";
import { PreventExit } from "components/PreventExit";
import { useNavigation } from "contexts/notificationsContext";
import { usePreventExit } from "hooks/usePreventExit";
import { useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import { useParams } from "react-router-dom";
import { getProjetistas } from "services/modules/projetistas";
import { getCadastrosN2PorEmpresaId, postSalvarCadastrosN2 } from "services/modules/cadastrosN2";
import { ISalvarCadastrosN2, ISerial, IEmail } from "shared/interfaces/cadastrosN2";
import { IProjetista } from "shared/interfaces/projetista";
import { ModalCadastroSerial } from "./components/modalCadastroSerial";
import { ModalCadastroEmail } from "./components/modalCadastroEmail";

export const CadastrosN2Form = () => {
  const { openNotification } = useNavigation();
  const params = useParams();
  
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [projetistas, setProjetistas] = useState<IProjetista[]>([]);
  const [filtroCadastro, setFiltroCadastro] = useState("");
  const [filtroSolicitacoes, setFiltroSolicitacoes] = useState("");
  
  const [acessoCadastroTipologias, setAcessoCadastroTipologias] = useState(false);
  const [acessoSolicitacoes, setAcessoSolicitacoes] = useState(false);
  const [modoEnvio, setModoEnvio] = useState(false);
  const [modoValidacao, setModoValidacao] = useState(false);
  const [modoHomologacao, setModoHomologacao] = useState(false);
  
  const [projetistasCadastroSelecionados, setProjetistasCadastroSelecionados] = useState<number[]>([]);
  const [projetistasSolicitacoesSelecionados, setProjetistasSolicitacoesSelecionados] = useState<number[]>([]);
  
  const [seriais, setSeriais] = useState<ISerial[]>([]);
  const [emails, setEmails] = useState<IEmail[]>([]);
  
  const [modalSerial, setModalSerial] = useState<{ isOpen: boolean; data?: ISerial }>({ isOpen: false });
  const [modalEmail, setModalEmail] = useState<{ isOpen: boolean; data?: IEmail }>({ isOpen: false });

  const { control, formState } = useForm({
    mode: "onBlur",
  });

  const { isDirty } = useFormState({ control });

  const {
    isOpenModalUnsaved,
    handleIsOpenModalUnsaved,
    handleLinkNavigateBlocked,
    handleOnCloseModalUnsavedEdit,
    linkNavigateBlocked,
    isGoBack,
  } = usePreventExit(isDirty);

  const projetistasFiltradosCadastro = projetistas.filter((p) =>
    p.nome.toLowerCase().includes(filtroCadastro.toLowerCase())
  );

  const projetistasFiltradosSolicitacoes = projetistas.filter((p) =>
    p.nome.toLowerCase().includes(filtroSolicitacoes.toLowerCase())
  );

  const carregarProjetistas = async () => {
    const result = await getProjetistas({ page: 1, pageSize: 1000 });
    setProjetistas(result.filter((p) => p.ativo));
  };

  const carregarDados = async () => {
    if (!params.id) return;
    
    setIsLoading(true);
    try {
      const dados = await getCadastrosN2PorEmpresaId(Number(params.id));
      if (dados) {
        setAcessoCadastroTipologias(dados.acessoCadastroTipologiasLiberadas);
        setAcessoSolicitacoes(dados.acessoSolicitacoesTipologias);
        // Compatibilidade com dados legados (modoUtilizacao único)
        if (dados.modoEnvio !== undefined) {
          setModoEnvio(dados.modoEnvio);
          setModoValidacao(dados.modoValidacao ?? false);
          setModoHomologacao(dados.modoHomologacao ?? false);
        } else if (dados.modoUtilizacao) {
          // Conversão de legado para novos flags
          setModoEnvio(dados.modoUtilizacao === 1);
          setModoValidacao(dados.modoUtilizacao === 2);
          setModoHomologacao(dados.modoUtilizacao === 3);
        }
        setProjetistasCadastroSelecionados(dados.projetistasCadastroTipologias);
        setProjetistasSolicitacoesSelecionados(dados.projetistasSolicitacoes);
        setSeriais(dados.seriais);
        setEmails(dados.emails);
      }
    } catch (error) {
      // Sem dados ainda
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    carregarProjetistas();
    carregarDados();
  }, [params.id]);

  const handleSelecionarTodosCadastro = () => {
    if (projetistasCadastroSelecionados.length === projetistas.length) {
      setProjetistasCadastroSelecionados([]);
    } else {
      setProjetistasCadastroSelecionados(projetistas.map((p) => p.idProjetista));
    }
  };

  const handleSelecionarTodosSolicitacoes = () => {
    if (projetistasSolicitacoesSelecionados.length === projetistas.length) {
      setProjetistasSolicitacoesSelecionados([]);
    } else {
      setProjetistasSolicitacoesSelecionados(projetistas.map((p) => p.idProjetista));
    }
  };

  const handleToggleProjetistaCadastro = (id: number) => {
    setProjetistasCadastroSelecionados((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleToggleProjetistaSolicitacao = (id: number) => {
    setProjetistasSolicitacoesSelecionados((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSalvarSerial = (serial: ISerial) => {
    if (modalSerial.data) {
      setSeriais((prev) =>
        prev.map((s) => (s.id === modalSerial.data!.id ? { ...serial, id: s.id } : s))
      );
    } else {
      setSeriais((prev) => [...prev, { ...serial, id: Date.now() }]);
    }
    setModalSerial({ isOpen: false });
  };

  const handleExcluirSerial = (id: number) => {
    setSeriais((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSalvarEmail = (email: IEmail) => {
    if (modalEmail.data) {
      setEmails((prev) =>
        prev.map((e) => (e.id === modalEmail.data!.id ? { ...email, id: e.id } : e))
      );
    } else {
      setEmails((prev) => [...prev, { ...email, id: Date.now() }]);
    }
    setModalEmail({ isOpen: false });
  };

  const handleExcluirEmail = (id: number) => {
    setEmails((prev) => prev.filter((e) => e.id !== id));
  };

  const handleModoEnvioChange = (checked: boolean) => {
    setModoEnvio(checked);
    // Se Envio for selecionado, desmarca Validação (mutuamente exclusivo)
    if (checked && modoValidacao) {
      setModoValidacao(false);
    }
  };

  const handleModoValidacaoChange = (checked: boolean) => {
    setModoValidacao(checked);
    // Se Validação for selecionada, desmarca Envio e Homologação (mutuamente exclusivo)
    if (checked) {
      setModoEnvio(false);
      setModoHomologacao(false);
    }
  };

  const handleModoHomologacaoChange = (checked: boolean) => {
    setModoHomologacao(checked);
    // Se Homologação for selecionada, desmarca Validação (mutuamente exclusivo)
    if (checked && modoValidacao) {
      setModoValidacao(false);
    }
  };

  const handleSubmitForm = async () => {
    // Validações
    if (acessoCadastroTipologias && projetistasCadastroSelecionados.length === 0) {
      openNotification({
        message: "Selecione ao menos um Projetista para Cadastro de Tipologias.",
        type: "error",
      });
      return;
    }

    if (acessoSolicitacoes) {
      if (projetistasSolicitacoesSelecionados.length === 0) {
        openNotification({
          message: "Selecione ao menos um Projetista para Solicitações de Tipologias.",
          type: "error",
        });
        return;
      }

      if (!modoEnvio && !modoValidacao && !modoHomologacao) {
        openNotification({
          message: "Selecione ao menos um Modo de Utilização.",
          type: "error",
        });
        return;
      }

      // Validação: Validação não pode ser combinada com outros modos
      if (modoValidacao && (modoEnvio || modoHomologacao)) {
        openNotification({
          message: "O modo 'Validação das tipologias' não pode ser combinado com outros modos. Selecione apenas Validação ou a combinação Envio + Homologação.",
          type: "error",
        });
        return;
      }

      if (modoEnvio && seriais.length === 0) {
        openNotification({
          message: "Configure ao menos um serial autorizado para o modo Envio.",
          type: "error",
        });
        return;
      }

      if ((modoValidacao || modoHomologacao) && emails.length === 0) {
        openNotification({
          message: "Configure ao menos um e-mail para notificação.",
          type: "error",
        });
        return;
      }
    }

    setIsLoadingSubmit(true);
    try {
      const dados: ISalvarCadastrosN2 = {
        empresaId: Number(params.id),
        acessoCadastroTipologiasLiberadas: acessoCadastroTipologias,
        acessoSolicitacoesTipologias: acessoSolicitacoes,
        modoEnvio: modoEnvio,
        modoValidacao: modoValidacao,
        modoHomologacao: modoHomologacao,
        projetistasCadastroTipologias: projetistasCadastroSelecionados,
        projetistasSolicitacoes: projetistasSolicitacoesSelecionados,
        seriais: seriais.map((s) => ({ serial: s.serial, projetistasIds: s.projetistasIds })),
        emails: emails.map((e) => ({ nomeDestinatario: e.nomeDestinatario, email: e.email })),
      };

      await postSalvarCadastrosN2(dados);
      openNotification({
        message: "Cadastros N2 salvos com sucesso!",
        type: "success",
      });
      await carregarDados();
    } catch (error: any) {
      openNotification({
        message: error?.message || "Erro ao salvar Cadastros N2",
        type: "error",
      });
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const projetistasPorIds = (ids: number[]) =>
    projetistas
      .filter((p) => ids.includes(p.idProjetista))
      .map((p) => p.nome)
      .join(", ");

  const SectionContainer: React.FC<{
    title: string;
    toggle?: React.ReactNode;
    children: React.ReactNode;
    description?: string;
  }> = ({ title, toggle, children, description }) => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.08)",
        border: "1px solid #E0E7FF",
        bgcolor: "#fff",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: "#3583B1", fontWeight: 600 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        {toggle}
      </Stack>
      {children}
    </Paper>
  );

  const SelectAllCheckbox = ({
    checked,
    indeterminate,
    onChange,
    label,
  }: {
    checked: boolean;
    indeterminate: boolean;
    onChange: () => void;
    label: string;
  }) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          onChange={onChange}
        />
      }
      label={
        <Typography sx={{ fontWeight: 700, color: "#3583B1", letterSpacing: 0.5 }}>
          {label.toUpperCase()}
        </Typography>
      }
      sx={{ mb: 1 }}
    />
  );

  const EmptyState = ({ message }: { message: string }) => (
    <Box
      sx={{
        py: 4,
        textAlign: "center",
        borderRadius: 2,
        border: "1px dashed #D0D7FF",
        bgcolor: "#F8FAFF",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  return (
    <PreventExit
      handleIsOpenModalUnsaved={handleIsOpenModalUnsaved}
      handleLinkNavigateBlocked={handleLinkNavigateBlocked}
      isOpenModalUnsaved={isOpenModalUnsaved}
      handleOnCloseModalUnsavedEdit={handleOnCloseModalUnsavedEdit}
      linkNavigateBlocked={linkNavigateBlocked}
      isGoBack={isGoBack}
    >
      <Grid container spacing={3}>
        {/* SEÇÃO 1: Acesso ao cadastro de tipologias liberadas para o mercado */}
        <Grid item xs={12}>
          <SectionContainer
            title="Acesso ao cadastro de tipologias liberadas para o mercado"
            toggle={
              <FormControlLabel
                control={
                  <Switch
                    checked={acessoCadastroTipologias}
                    onChange={(e) => setAcessoCadastroTipologias(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 600, color: "text.secondary" }}>
                    Habilitar acesso
                  </Typography>
                }
                labelPlacement="start"
                sx={{ m: 0 }}
              />
            }
          >
            {acessoCadastroTipologias ? (
              <Box
                sx={{
                  width: { xs: "100%", sm: 400 },
                  bgcolor: "#fff",
                  borderRadius: 4,
                  boxShadow: "0px 4px 12px rgba(23, 42, 89, 0.06)",
                  border: "1px solid rgba(223, 230, 242, 0.9)",
                  p: 2.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#01294C", mb: 1.5 }}>
                  Seleção de projetistas
                </Typography>

                <Box
                  sx={{
                    mb: 2,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: "rgba(235, 242, 255, 0.95)",
                    border: "1px solid rgba(205, 220, 247, 1)",
                    height: 42,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <input
                    value={filtroCadastro}
                    onChange={(e) => setFiltroCadastro(e.target.value)}
                    placeholder="Busque a projetista"
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      width: "100%",
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#01294C",
                    }}
                  />
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "rgba(209, 226, 248, 1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Search sx={{ color: "#4E92C3", fontSize: 18 }} />
                  </Box>
                </Box>

                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(53, 131, 177, 0.45)",
                      borderRadius: 3,
                    },
                  }}
                >
                  <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        bgcolor: "transparent",
                      }}
                  >
                      <Typography sx={{ fontWeight: 700, color: "#3583B1", letterSpacing: 0.6 }}>
                      SELECIONAR TODOS
                    </Typography>
                    <Checkbox
                      checked={projetistasCadastroSelecionados.length === projetistas.length && projetistas.length > 0}
                      indeterminate={
                        projetistasCadastroSelecionados.length > 0 &&
                        projetistasCadastroSelecionados.length < projetistas.length
                      }
                      onChange={handleSelecionarTodosCadastro}
                      sx={{
                        color: "#75B0C7",
                        "&.Mui-checked": { color: "#3C90AD" },
                        "& .MuiSvgIcon-root": { fontSize: 20 },
                      }}
                    />
                  </Box>

                  {projetistasFiltradosCadastro.map((projetista) => (
                    <Box
                      key={projetista.idProjetista}
                      sx={{
                        px: 1.5,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: "#3C90AD" }}>
                        {projetista.nome}
                      </Typography>
                      <Checkbox
                        checked={projetistasCadastroSelecionados.includes(projetista.idProjetista)}
                        onChange={() => handleToggleProjetistaCadastro(projetista.idProjetista)}
                        sx={{
                          color: "#75B0C7",
                          "&.Mui-checked": { color: "#3C90AD" },
                          "& .MuiSvgIcon-root": { fontSize: 20 },
                        }}
                      />
                    </Box>
                  ))}

                  {projetistasFiltradosCadastro.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2 }}>
                      Nenhum projetista encontrado.
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <EmptyState message="Ative o acesso para configurar os projetistas liberados." />
            )}
          </SectionContainer>
        </Grid>

        <Grid item xs={12}>
          <SectionContainer
            title="Solicitações de Tipologias"
            toggle={
              <FormControlLabel
                control={
                  <Switch
                    checked={acessoSolicitacoes}
                    onChange={(e) => setAcessoSolicitacoes(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600, color: "text.secondary" }}>Habilitar solicitações</Typography>}
                labelPlacement="start"
                sx={{ m: 0 }}
              />
            }
          >
            {acessoSolicitacoes ? (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#001A29" }}>
                    Modo de utilização
                  </Typography>
                  <Stack spacing={1.5} mt={2}>
                    {[
                      {
                        key: "envio",
                        label: "Envio das tipologias",
                        checked: modoEnvio,
                        onChange: handleModoEnvioChange,
                        disabled: modoValidacao,
                      },
                      {
                        key: "validacao",
                        label: "Validação das tipologias",
                        checked: modoValidacao,
                        onChange: handleModoValidacaoChange,
                        disabled: modoEnvio || modoHomologacao,
                      },
                      {
                        key: "homologacao",
                        label: "Homologação das tipologias",
                        checked: modoHomologacao,
                        onChange: handleModoHomologacaoChange,
                        disabled: modoValidacao,
                      },
                    ].map((toggle) => (
                      <Box
                        key={toggle.key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 0.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, color: "#0B5F8A" }}>
                          {toggle.label}
                        </Typography>
                        <Switch
                          checked={toggle.checked}
                          onChange={(event) => toggle.onChange(event.target.checked)}
                          disabled={toggle.disabled}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#2F7EA0",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "rgba(47, 126, 160, 0.45)",
                            },
                            "& .MuiSwitch-track": {
                              backgroundColor: "rgba(205, 220, 236, 1)",
                              opacity: 1,
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {/* Seleção de projetistas - sempre visível quando acesso está ativo */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "50%" },
                    bgcolor: "#fff",
                    borderRadius: 4,
                    boxShadow: "0px 4px 12px rgba(23, 42, 89, 0.06)",
                    border: "1px solid rgba(223, 230, 242, 0.9)",
                    p: 2.5,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#01294C", mb: 1 }}>
                    Seleção de projetistas
                  </Typography>
                      <Box
                        sx={{
                          mb: 2,
                          px: 1,
                          borderRadius: 1,
                          bgcolor: "rgba(235, 242, 255, 0.95)",
                          border: "1px solid rgba(205, 220, 247, 1)",
                          height: 42,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <input
                          value={filtroSolicitacoes}
                          onChange={(e) => setFiltroSolicitacoes(e.target.value)}
                          placeholder="Busque o projetista"
                          style={{
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            width: "100%",
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#01294C",
                          }}
                        />
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: "rgba(209, 226, 248, 1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Search sx={{ color: "#4E92C3", fontSize: 18 }} />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          maxHeight: 280,
                          overflowY: "auto",
                          "&::-webkit-scrollbar": { width: 6 },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(53, 131, 177, 0.45)",
                            borderRadius: 3,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: "transparent",
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: "#3583B1", letterSpacing: 0.6 }}>
                            SELECIONAR TODOS
                          </Typography>
                          <Checkbox
                            checked={
                              projetistasSolicitacoesSelecionados.length === projetistas.length && projetistas.length > 0
                            }
                            indeterminate={
                              projetistasSolicitacoesSelecionados.length > 0 &&
                              projetistasSolicitacoesSelecionados.length < projetistas.length
                            }
                            onChange={handleSelecionarTodosSolicitacoes}
                            sx={{
                              color: "#75B0C7",
                              "&.Mui-checked": { color: "#3C90AD" },
                              "& .MuiSvgIcon-root": { fontSize: 20 },
                            }}
                          />
                        </Box>
                        {projetistasFiltradosSolicitacoes.map((projetista) => (
                          <Box
                            key={projetista.idProjetista}
                            sx={{
                              px: 1.5,
                              py: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, color: "#3C90AD" }}>
                              {projetista.nome}
                            </Typography>
                            <Checkbox
                              checked={projetistasSolicitacoesSelecionados.includes(projetista.idProjetista)}
                              onChange={() => handleToggleProjetistaSolicitacao(projetista.idProjetista)}
                              sx={{
                                color: "#75B0C7",
                                "&.Mui-checked": { color: "#3C90AD" },
                                "& .MuiSvgIcon-root": { fontSize: 20 },
                              }}
                            />
                          </Box>
                        ))}
                        {projetistasFiltradosSolicitacoes.length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2 }}>
                            Nenhum projetista encontrado.
                          </Typography>
                        )}
                      </Box>
                </Box>

                {/* Seriais - apenas quando modo Envio está ativo */}
                {modoEnvio && (
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 4,
                      boxShadow: "0px 4px 12px rgba(23, 42, 89, 0.06)",
                      border: "1px solid rgba(223, 230, 242, 0.9)",
                      p: 2.5,
                      minHeight: 340,
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" mb={2} spacing={1.5} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#01294C" }}>
                        Seriais CEM PRO - Edição e envio das tipologias
                      </Typography>
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => setModalSerial({ isOpen: true })}
                        sx={{ 
                          py: 0.5,
                          px: 2,
                          minHeight: "auto",
                          height: "32px"
                        }}
                      >
                        ADICIONAR
                      </Button>
                    </Stack>
                    {seriais.length === 0 ? (
                      <EmptyState message="Nenhum serial cadastrado." />
                    ) : (
                      <Stack spacing={1.5}>
                        {seriais.map((serial) => (
                          <Box key={serial.id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  bgcolor: "rgba(248, 250, 255, 1)",
                                  borderRadius: 1.5,
                                  p: 1.5,
                                  border: "1px solid rgba(226, 232, 240, 0.8)",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#3C90AD",
                                    fontWeight: 600,
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Serial
                                </Typography>
                                <Typography sx={{ fontWeight: 600, color: "#01294C" }}>
                                  {serial.serial}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setModalSerial({ isOpen: true, data: serial })}
                                  sx={{ p: 0.75 }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => serial.id !== undefined && handleExcluirSerial(serial.id!)}
                                  sx={{ p: 0.75 }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#3583B1",
                                display: "block",
                                mt: 0.5,
                                ml: 1.5,
                              }}
                            >
                              Campo obrigatório
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}

                {(modoValidacao || modoHomologacao) && (
                  <Box>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" mb={2} spacing={1.5} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        E-mail(s) que receberão notificações
                      </Typography>
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => setModalEmail({ isOpen: true })}
                        sx={{ 
                          py: 0.5,
                          px: 2,
                          minHeight: "auto",
                          height: "32px"
                        }}
                      >
                        ADICIONAR E-MAIL
                      </Button>
                    </Stack>
                    {emails.length === 0 ? (
                      <EmptyState message="Nenhum e-mail configurado." />
                    ) : (
                      <Stack spacing={1.5}>
                        {emails.map((email) => (
                          <Box key={email.id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 2,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Box
                                  sx={{
                                    bgcolor: "rgba(248, 250, 255, 1)",
                                    borderRadius: 1.5,
                                    p: 1.5,
                                    border: "1px solid rgba(226, 232, 240, 0.8)",
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#3C90AD",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    Nome do destinatário
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600, color: "#01294C" }}>
                                    {email.nomeDestinatario}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#3583B1",
                                    display: "block",
                                    ml: 1.5,
                                  }}
                                >
                                  Campo obrigatório
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Box
                                  sx={{
                                    bgcolor: "rgba(248, 250, 255, 1)",
                                    borderRadius: 1.5,
                                    p: 1.5,
                                    border: "1px solid rgba(226, 232, 240, 0.8)",
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#3C90AD",
                                      fontWeight: 600,
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    E-mail
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600, color: "#01294C" }}>
                                    {email.email}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#3583B1",
                                    display: "block",
                                    ml: 1.5,
                                  }}
                                >
                                  Campo obrigatório
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => email.id !== undefined && handleExcluirEmail(email.id!)}
                                sx={{ p: 0.75, mt: 0.5 }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </Stack>
            ) : (
              <EmptyState message="Ative as solicitações para configurar modos, seriais e e-mails." />
            )}
          </SectionContainer>
        </Grid>

        {/* Botão Salvar */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <LoadingButton
              variant="contained"
              endIcon={<Save />}
              onClick={handleSubmitForm}
              loading={isLoadingSubmit}
            >
              Salvar
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>

      {/* Modal Serial */}
      {modalSerial.isOpen && (
        <ModalCadastroSerial
          isOpen={modalSerial.isOpen}
          onClose={() => setModalSerial({ isOpen: false })}
          onSave={handleSalvarSerial}
          serialData={modalSerial.data}
          projetistas={projetistas}
          projetistasSelecionadosNoBloco={projetistasSolicitacoesSelecionados}
        />
      )}

      {/* Modal E-mail */}
      {modalEmail.isOpen && (
        <ModalCadastroEmail
          isOpen={modalEmail.isOpen}
          onClose={() => setModalEmail({ isOpen: false })}
          onSave={handleSalvarEmail}
          emailData={modalEmail.data}
        />
      )}
    </PreventExit>
  );
};
